import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../services/api';
import V2Header from '../../components/v2/layout/V2Header';
import V2Container from '../../components/v2/layout/V2Container';
import FilterBar from '../../components/v2/ui/FilterBar';
import ShiftCard from '../../components/v2/cards/ShiftCard';
import EmptyState from '../../components/v2/ui/EmptyState';
import '../../v2-theme.css';
import './WorkerDashboard.css';

/**
 * Worker Dashboard Page
 * Matches worker-dashboard.html exactly
 * Features: Browse shifts, filters, claim with overlap validation, my shifts
 */
const WorkerDashboard = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { socket } = useSocket();

  const [availableShifts, setAvailableShifts] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [filters, setFilters] = useState({
    position: '',
    date: '',
    minPay: ''
  });
  const [stats, setStats] = useState({
    shiftsCompleted: 0,
    hoursWorked: 0,
    totalEarned: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Position filter options
  const positionOptions = [
    { value: '', label: 'All Positions' },
    { value: 'dishwasher', label: 'Dishwasher' },
    { value: 'prep-cook', label: 'Prep Cook' },
    { value: 'line-cook', label: 'Line Cook' },
    { value: 'sous-chef', label: 'Sous Chef' },
    { value: 'head-chef', label: 'Head Chef' },
    { value: 'event-cook', label: 'Event Cook' },
    { value: 'banquet', label: 'Banquet' }
  ];

  // Min pay filter options
  const payOptions = [
    { value: '', label: 'Any Pay' },
    { value: '20', label: '$20+/hr' },
    { value: '25', label: '$25+/hr' },
    { value: '30', label: '$30+/hr' },
    { value: '40', label: '$40+/hr' }
  ];

  // Filter configuration for FilterBar component
  const filterConfig = [
    {
      type: 'select',
      name: 'position',
      label: 'Position',
      options: positionOptions
    },
    {
      type: 'date',
      name: 'date',
      label: 'Date'
    },
    {
      type: 'select',
      name: 'minPay',
      label: 'Min Hourly Pay',
      options: payOptions
    }
  ];

  // Fetch available shifts
  const fetchShifts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/jobs/available');
      setAvailableShifts(response.data.jobs || []);
      setError('');
    } catch (err) {
      console.error('Error fetching shifts:', err);
      setError('Failed to load available shifts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch worker's claimed shifts
  const fetchMyShifts = async () => {
    try {
      const response = await api.get('/applications');
      // Transform applications into shifts format
      const applications = response.data.applications || [];
      const shifts = applications
        .filter(app => app.status === 'pending' || app.status === 'accepted')
        .map(app => ({
          ...app.job_postings,
          application_id: app.id,
          application_status: app.status
        }));
      setMyShifts(shifts);
    } catch (err) {
      console.error('Error fetching my shifts:', err);
    }
  };

  // Fetch worker stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/workers/stats');
      setStats(response.data || { shiftsCompleted: 0, hoursWorked: 0, totalEarned: 0 });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Check for shift overlap
  const hasOverlap = (newShift) => {
    const newDate = new Date(newShift.shift_date).toDateString();
    const newStart = newShift.start_time;
    const newEnd = newShift.end_time;

    for (let shift of myShifts) {
      const existingDate = new Date(shift.shift_date).toDateString();

      // Only check if same date
      if (existingDate === newDate) {
        const existingStart = shift.start_time;
        const existingEnd = shift.end_time;

        // Shifts overlap if: new_start < existing_end AND new_end > existing_start
        if (newStart < existingEnd && newEnd > existingStart) {
          return true;
        }
      }
    }

    return false;
  };

  // Claim shift
  const handleClaimShift = async (shift) => {
    // Check for overlap
    if (hasOverlap(shift)) {
      alert('Cannot claim shift: You already have a shift scheduled during this time.');
      return;
    }

    if (!window.confirm('Claim this shift?')) {
      return;
    }

    try {
      await api.post('/applications', {
        job_id: shift.id
      });

      // Refresh shifts
      await fetchShifts();
      await fetchMyShifts();

      alert('Shift claimed successfully!');
    } catch (err) {
      console.error('Error claiming shift:', err);
      alert(err.response?.data?.error || 'Failed to claim shift. Please try again.');
    }
  };

  // Cancel shift
  const handleCancelShift = async (shift) => {
    if (!window.confirm('Are you sure you want to cancel this shift?')) {
      return;
    }

    try {
      await api.delete(`/applications/${shift.application_id}`);

      // Refresh shifts
      await fetchShifts();
      await fetchMyShifts();

      alert('Shift cancelled successfully.');
    } catch (err) {
      console.error('Error cancelling shift:', err);
      alert(err.response?.data?.error || 'Failed to cancel shift. Please try again.');
    }
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Filter shifts based on current filters
  const getFilteredShifts = () => {
    return availableShifts.filter(shift => {
      // Position filter
      if (filters.position && shift.position !== filters.position) {
        return false;
      }

      // Date filter
      if (filters.date) {
        const shiftDate = new Date(shift.shift_date).toISOString().split('T')[0];
        if (shiftDate !== filters.date) {
          return false;
        }
      }

      // Min pay filter
      if (filters.minPay) {
        const minPay = parseFloat(filters.minPay);
        if (shift.hourly_rate < minPay) {
          return false;
        }
      }

      return true;
    });
  };

  // Initial data fetch
  useEffect(() => {
    if (user) {
      fetchShifts();
      fetchMyShifts();
      fetchStats();
    }
  }, [user]);

  // Socket.IO listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    // Listen for new shifts posted
    socket.on('shift_posted', (shift) => {
      setAvailableShifts(prev => [shift, ...prev]);
    });

    // Listen for shifts claimed by others
    socket.on('shift_claimed', (jobId) => {
      setAvailableShifts(prev => prev.filter(shift => shift.id !== jobId));
    });

    // Listen for shift cancellations
    socket.on('shift_cancelled', (jobId) => {
      fetchShifts(); // Refresh available shifts
    });

    return () => {
      socket.off('shift_posted');
      socket.off('shift_claimed');
      socket.off('shift_cancelled');
    };
  }, [socket]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await signOut();
      navigate('/');
    }
  };

  const filteredShifts = getFilteredShifts();

  return (
    <div className="worker-dashboard-page">
      <V2Header
        title="Hands On The Fly"
        userName={user?.first_name || 'Worker'}
        userType="worker"
        profilePath="/profile/worker"
        onLogout={handleLogout}
      />

      <V2Container maxWidth="lg">
        <div className="dashboard-header">
          <h2 className="v2-heading-xl">Available Shifts</h2>
          <p className="v2-text-silver">Browse and claim shifts</p>
        </div>

        {/* Filter Bar */}
        <FilterBar
          filters={filterConfig}
          values={filters}
          onChange={handleFilterChange}
        />

        {/* Error Message */}
        {error && (
          <div className="v2-error-message">{error}</div>
        )}

        {/* Available Shifts Grid */}
        {loading ? (
          <div className="v2-loading">Loading shifts...</div>
        ) : filteredShifts.length === 0 ? (
          <EmptyState message="No available shifts match your filters" />
        ) : (
          <div className="shifts-grid">
            {filteredShifts.map(shift => (
              <ShiftCard
                key={shift.id}
                shift={shift}
                userType="worker"
                onClaim={() => handleClaimShift(shift)}
              />
            ))}
          </div>
        )}

        {/* My Upcoming Shifts & Stats */}
        <div className="dashboard-grid">
          {/* My Upcoming Shifts */}
          <div className="dashboard-section">
            <h3 className="v2-heading-lg">My Upcoming Shifts</h3>
            {myShifts.length === 0 ? (
              <EmptyState message="No upcoming shifts" />
            ) : (
              <div className="my-shifts-list">
                {myShifts.map(shift => (
                  <ShiftCard
                    key={shift.id}
                    shift={shift}
                    userType="worker"
                    isClaimed={true}
                    onCancel={() => handleCancelShift(shift)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="dashboard-section">
            <h3 className="v2-heading-lg">Quick Stats</h3>
            <div className="stats-list">
              <div className="stat-item">
                <strong>Shifts Completed:</strong> {stats.shiftsCompleted}
              </div>
              <div className="stat-item">
                <strong>Hours Worked:</strong> {stats.hoursWorked}
              </div>
              <div className="stat-item">
                <strong>Total Earned:</strong> ${stats.totalEarned.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </V2Container>
    </div>
  );
};

export default WorkerDashboard;
